import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import { searchMetaobjectQuery } from "@repo/shopify/src/queries/metaobject-gql";
import type { ISearchMetaObjectsQuery } from "@repo/shopify/src/queries/metaobject-gql";
import { predictiveSearchQuery } from "@repo/shopify/src/queries/search-gql";
import type { IPredictiveSearchProduct, IPredictiveSearchQuery } from "@repo/shopify/src/queries/search-gql";
import type { SearchQuerySuggestion } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import Button from "@components/atoms/Button";
import Dim from "@components/atoms/Dim";
import Icon from "@components/atoms/Icon";
import Input from "@components/atoms/Input/Input";
import LoadingIcon from "@components/atoms/LoadingIcon";
import Logo from "@components/atoms/Logo";

import ProductGrid from "@organisms/ProductGrid";

import useLayout from "@hooks/reducerHooks/useLayout";

import { IconName } from "@const/enum";
import { URLS } from "@const/urls";

import { PRODUCT_TAG, getCollectionsAllUrl } from "@utils/archive-sale";

function Search() {
  const navigate = useNavigate();

  const { closeSearch } = useLayout();
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState<string>("");
  const [backupSuggestions, setBackupSuggestions] = useState<SearchQuerySuggestion[]>([]);
  const [suggestions, setSuggestions] = useState<SearchQuerySuggestion[]>([]);

  const { register, handleSubmit, reset, setFocus, getValues } = useForm();

  // 검색 컴포넌트가 열렸을 때 바로 인풋에 포커스
  useEffect(() => {
    setFocus("search");
  }, []);

  const [loadSearchResult, { data: searchData, loading: searchLoading }] = useLazyQuery<IPredictiveSearchQuery>(
    gql(predictiveSearchQuery.query),
    {
      variables: {
        query: searchValue,
        limit: 10,
        limitScope: "EACH",
        searchableFields: ["TITLE", "VENDOR", "PRODUCT_TYPE", "BODY"],
        types: ["QUERY", "PRODUCT"],
        unavailableProducts: "LAST",
      },
      onCompleted: (data) => {
        const regex = new RegExp(searchValue, "i");

        const newSuggestData = data.predictiveSearch.queries.filter(
          (item) => !item.text.includes("employee") && !item.text.includes("test") && !item.text.includes("draft"),
        );

        // 추천 검색어에 상품명, 브랜드명 추가
        searchData?.predictiveSearch.products
          .filter((item: IPredictiveSearchProduct) => item.hidden?.value === "false")
          .slice(0, 4)
          .forEach((product) => {
            if (regex.test(product.title)) {
              newSuggestData.push({ text: product.title.toLowerCase() } as SearchQuerySuggestion);
            }

            if (regex.test(product.vendor)) {
              newSuggestData.push({ text: product.vendor.toLowerCase() } as SearchQuerySuggestion);
            }
          });

        const uniqueArray = [...new Map(newSuggestData.map((item) => [item.text, item])).values()].slice(0, 4);
        // setSuggestions(uniqueArray);
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][PredictiveSearch] Failed to query", error);
        throw new Error(`[SHOPIFY][PredictiveSearch] Failed to query : ${JSON.stringify(error)}`);
      },
    },
  );

  const products = useMemo(
    () =>
      searchData?.predictiveSearch.products
        .filter((item: IPredictiveSearchProduct) => item.hidden?.value === "false")
        .filter((item: IPredictiveSearchProduct) => item.tags.includes(PRODUCT_TAG))
        .map((item) => ({ node: item }))
        .slice(0, 4) || [],
    [searchData],
  );

  // Predictive Search 실행 함수, 인풋 입력될 때마다 실행됨
  const handleSearch = () => {
    const value = getValues("search");
    setSearchValue(value);

    setTimeout(() => {
      void loadSearchResult({ variables: { query: value } });
    }, 200);
  };

  // 기본 추천 검색어 쿼리 (MD팀에서 어드민 내 Metaobject로 변경)
  const { loading } = useQuery<ISearchMetaObjectsQuery>(gql(searchMetaobjectQuery.query), {
    variables: { type: "archive_search_suggestions", first: 10, reverse: true },
    onCompleted: (data) => {
      const suggestList = JSON.parse(data.metaobjects.edges[0]?.node?.value?.value || "[]") as string[];
      const newList = suggestList.map((item: string) => ({ text: item }));
      setBackupSuggestions(newList as SearchQuerySuggestion[]);
    },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][MetaobjectsSearch] Failed to query", error);
      throw new Error(`[SHOPIFY][MetaobjectsSearch] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  // 기본 추천 검색어가 보이는 로직
  useEffect(() => {
    if (!searchValue || suggestions.length < 1) {
      setSuggestions(backupSuggestions);
    }
  }, [suggestions, backupSuggestions, searchValue]);

  // 검색 결과 페이지로 이동
  const onSubmit = handleSubmit(() => {
    navigate(`${URLS.SEARCH}?q=${searchValue}`);
    reset();
    closeSearch();
  });

  return (
    <div>
      <div className="z-aboveChannelTalk pc:h-auto fixed top-0 h-screen w-full bg-white text-black">
        <div className="pc:grid pc:w-full pc:grid-cols-12 flex w-screen flex-col items-center gap-5 px-5 py-2.5">
          <div className="col-span-3 justify-self-start">
            <Logo />
          </div>
          <form className="col-span-6 w-full" onSubmit={onSubmit}>
            <Input
              {...register("search", {
                required: t("search.alert"),
                onChange: handleSearch,
              })}
              className="text-black"
              inputMode="search"
              leftIconNode={<Icon className="pc:w-4.5 size-6" name={IconName.Search_700} />}
              placeholder="Search"
              value={searchValue}
              variant="grey"
            />
          </form>
          <div className="pc:static pc:col-span-3 pc:justify-self-end fixed bottom-5 z-20">
            <Button
              className="rounded-md"
              onClick={() => {
                closeSearch();
              }}
              styleType="secondary"
              variant="containedIcon"
            >
              <Icon height="0.875rem" name={IconName.Close_100} width="0.875rem" />
            </Button>
          </div>
        </div>
        <div className="h-mobileSearch pc:fixed pc:grid pc:h-auto pc:w-full pc:grid-cols-12 pc:gap-5 pc:pb-7.5 flex w-screen flex-col gap-5 overflow-y-scroll bg-white p-5 pb-18">
          {loading ? (
            <LoadingIcon className="col-span-12 flex h-67.5 items-center justify-center" type="spinner" />
          ) : (
            <>
              <div className="pc:col-start-4 pc:col-end-10pc:col-span-3 w-full">
                <div className="pc:text-sm mb-5">추천 검색어</div>
                <div className="pc:text-base mb-5 flex flex-col gap-2.5 text-2xl">
                  {suggestions.map((item) => (
                    <Link className="capitalize" key={item.text} onClick={closeSearch} to={`/search?q=${item.text}`}>
                      {item.text}
                    </Link>
                  ))}
                </div>
                <Link
                  className="underline"
                  onClick={closeSearch}
                  to={searchValue ? `${URLS.SEARCH}?q=${searchValue}` : getCollectionsAllUrl()}
                >
                  {t("search.view_all")}
                </Link>
              </div>
              {/*{searchValue && products.length ? (*/}
              {/*  <div className="pc:col-span-9">*/}
              {/*    <ProductGrid products={products} />*/}
              {/*  </div>*/}
              {/*) : (*/}
              {/*  searchValue && <span className="col-span-9">{searchValue} is not founded.</span>*/}
              {/*)}*/}
            </>
          )}
        </div>
      </div>
      <Dim onClick={closeSearch} />
    </div>
  );
}

export default Search;
